import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { useMfaVerify } from '../api/useMfa'

// Deliberately a single input rather than react-otp-input-hook's per-digit
// boxes. That hook's register() calls useCallback internally, so it *is* a hook:
// calling it six times in one render and zero times in another (this form swaps
// between digit boxes and a recovery-code field) changes the hook count and
// React tears the tree down with error #310. A single field also pastes cleanly,
// which is how most people enter a code from an authenticator app.

/**
 * Second step of a staff sign-in: the password was accepted, but no session
 * exists until a code from the authenticator app is verified.
 *
 * Also accepts a recovery code, which is the only way back in from a lost phone
 * short of shell access to the server.
 */
export const MfaChallengeForm = () => {
    const [otp, setOtp] = useState('')
    const [useRecoveryCode, setUseRecoveryCode] = useState(false)
    const [recoveryCode, setRecoveryCode] = useState('')

    const { mutate, isLoading, error, reset } = useMfaVerify()

    const code = useRecoveryCode ? recoveryCode : otp

    const submit = (e) => {
        e.preventDefault()
        if (!code) return
        reset()
        mutate({ code })
    }

    return (
        <form className='flex flex-col items-center gap-4 w-full' onSubmit={submit}>
            <p className='text-base font-normal text-center max-w-md'>
                {useRecoveryCode
                    ? 'Enter one of the recovery codes you saved when you set up two-factor authentication.'
                    : 'Enter the 6-digit code from your authenticator app.'}
            </p>

            {useRecoveryCode ? (
                <input
                    autoFocus
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value)}
                    placeholder='XXXXX-XXXXX'
                    className='login-form text-center tracking-widest uppercase'
                    maxLength={16}
                />
            ) : (
                <input
                    autoFocus
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder='000000'
                    inputMode='numeric'
                    autoComplete='one-time-code'
                    maxLength={6}
                    className='bg-black/50 backdrop-blur-2xl border text-white text-center text-2xl md:text-3xl tracking-[0.25em] indent-[0.25em] w-full max-w-xs p-3'
                />
            )}

            <label className='login-form error text-center'>{error?.message}</label>

            <button
                type='submit'
                disabled={isLoading || !code}
                className='flex flex-row gap-2 items-center justify-center p-4 w-full md:w-1/2 mx-auto hover:outline disabled:opacity-50'
                style={{ background: 'linear-gradient(142deg,#03CEFF 0%,#C91FFF 100%)' }}
            >
                <span>Verify</span>
                {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
            </button>

            <button
                type='button'
                className='text-base font-normal underline hover:no-underline'
                onClick={() => {
                    reset()
                    setUseRecoveryCode((v) => !v)
                }}
            >
                {useRecoveryCode ? 'Use my authenticator app instead' : 'I lost my device, use a recovery code'}
            </button>
        </form>
    )
}
